import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import snakecaseKeys from 'snakecase-keys';

import { clickAsLink } from 'helpers/link_helpers';
import MergeRecordsModal from 'components/modals/merge_records_modal';
import ManagedCheckbox from 'components/form/managed_input/checkbox';
import { Button } from 'components/ui/button';
import { allListedOn, defaultKeys, valuesDiffer } from '../merge_rows';

import { startCase } from 'lodash';
import { Alert } from 'react-bootstrap-5';

// NOT exported
const companyAttributes = [
  'name',
  'url',
  'email',
  'facebookUrl',
  'twitterUrl',
  'skypeName',
  'referralSource',
  'industry'
];

export default class MergeCompanies extends React.Component {
  static propTypes = {
    companies: PropTypes.array.isRequired,
    companiesIndex: PropTypes.string.isRequired,
    mergeUrl: PropTypes.string.isRequired,
  };

  constructor(props) {
    super(props);

    this.state = {
      showMergeModal: false,
      showErrorPane: false,
      keys: defaultKeys(props.companies, companyAttributes)
    };
    this.errors = [];
    this.requiredFields = [];
  }

  findSelectedCompany = (id) => {
    return this.props.companies.find(company => company.id === id);
  }

  handleCancelModal = () => {
    this.setState({ showMergeModal: false });
  }

  handleErrorPaneDismiss = () => {
    this.setState({ showErrorPane: false });
  }

  handleSubmit = () => {
    return axios.post(this.props.mergeUrl, {
      mappings: snakecaseKeys(this.state.keys),
      company_ids: this.props.companies.map(company => company.id)
    }).then((response) => {
      window.location = response.data.nextUrl;
    }).catch((error) => {
      this.errors = error.response.data.errors;
      this.showErrorPane();
      this.handleCancelModal();
    });
  }

  onSelectAll = (e) => {
    const id = parseInt(e.target.value);
    const keys = {};

    companyAttributes.map((key) => {
      keys[key] = id;
    });

    this.setState({ keys: keys });
  }

  onSelectKey = (id, key) => {
    this.setState(prevState => ({
      keys: {
        ...prevState.keys,
        [key]: parseInt(id)
      }
    }));
  }

  requiredKeysSelected = () => {
    const required = this.requiredFields.every((key) => {
      if (this.shouldDisplayRow(key)) {
        return this.state.keys[key] != null;
      } else {
        return true;
      }
    });

    return required;
  }

  selectedAll = (id) => {
    const listed = companyAttributes.filter(key => this.shouldDisplayRow(key));
    return allListedOn(this.state.keys, listed, parseInt(id));
  }

  shouldDisplayRow(key) {
    return valuesDiffer(this.props.companies, key);
  }

  showErrorPane = () => {
    this.setState({ showErrorPane: true });
  }

  showMergeModal = () => {
    this.setState({ showMergeModal: true });
  }

  showCompanyIndex = (e) => {
    clickAsLink(window, this.props.companiesIndex, e);
  }

  render() {
    const disabled = !this.requiredKeysSelected();
    return (
      <div className="container">
        <div className="d-flex flex-column gap-4 py-4">
          <div>
            <Button className="ps-0" variant="ghost" onClick={this.showCompanyIndex}>
              &lt; Back to Companies
            </Button>
            <h6>
              Merge Companies
            </h6>
          </div>
          <div className="container bg-white p-4 border rounded">
            {this.renderErrorPane()}
            <p className="fs-large">
              In the table below, you will see all conflicting information
              between the companies you have selected to merge. For each
              row, please select which data you would like to keep.
            </p>
            <p className="fs-large">
              Please note that all associations will be preserved.
            </p>
            <div>
              <div className="table-container">
                <table className="table table-responsive text-nowrap">
                  <thead>
                    {this.renderHeaderColumns()}
                  </thead>
                  <tbody>
                    {this.renderTableContent()}
                  </tbody>
                </table>
              </div>
              <div className="d-flex justify-content-end mt-4 gap-2">
                <Button label="Cancel" variant="ghost" onClick={this.showCompanyIndex} />
                <Button disabled={disabled} label="Merge" onClick={this.showMergeModal} />
              </div>
              {this.renderMergeModal()}
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderHeaderColumns() {
    const companyHeaders = [];
    this.props.companies.map((company, index) => {
      companyHeaders.push(
        <th key={index}>
          <ManagedCheckbox
            checked={this.selectedAll(company.id)}
            label={`Company ${index + 1}`}
            name={`selectAll-${company.id}`}
            onChange={() => {}}
            onValueChange={() => this.onSelectAll({ target: { value: company.id } })}
          />
        </th>
      );
    });

    return (
      <tr>
        <th> Fields </th>
        {companyHeaders}
        <th> Merged Company </th>
      </tr>
    );
  }

  renderTableContent() {
    const rows = [];
    const keysToDelete = [];
    companyAttributes.map((key) => {
      if (this.shouldDisplayRow(key)) {
        const companyCells = [];
        this.props.companies.map((company) => {
          const value = Array.isArray(company[key]) ? company[key].join(', ') : company[key];

          companyCells.push(
            <td key={company.id + key}>
              <ManagedCheckbox
                checked={this.state.keys[key] === company.id}
                key={key}
                label={value}
                name={`${key}-${company.id}`}
                onChange={() => {}}
                onValueChange={() => this.onSelectKey(company.id, key)}
              />
            </td>
          );
        });

        const id = this.state.keys[key];
        const company = this.findSelectedCompany(id);
        const newVal = id ? (company[key] || '') : '';

        rows.push(
          <tr key={key}>
            <td>
              {startCase(key)}
              {this.requiredFields.includes(key) && <span className="required"> * </span> }
            </td>
            {companyCells}
            <td> {newVal} </td>
          </tr>
        );
      } else keysToDelete.push(key);
    });
    return rows;
  }

  renderMergeModal() {
    return (
      <MergeRecordsModal
        recordType={'Companies'}
        shown={this.state.showMergeModal}
        onCancel={this.handleCancelModal}
        onSubmit={this.handleSubmit}
      />
    );
  }

  renderErrorPane() {
    return (
      <Alert dismissible show={this.state.showErrorPane} variant="danger" onClose={this.handleErrorPaneDismiss}>
        <Alert.Heading>
          Unable to merge companies
        </Alert.Heading>
        <ul>
          {this.errors.map((error, index) => <li key={index}>{error}</li>)}
        </ul>
      </Alert>
    );
  }

}
